/**
 * Hook for testing integration connection in discovery forms.
 *
 * Used only by: GCP Vertex AI, Agent Force, and Copilot Studio discovery forms.
 * Do not use for AWS or other integration types.
 */

import { integrationApi } from "@repo/api";
import { useCallback, useState } from "react";

/** Payload shape for the test connection API (matches integrationApi.testConnectionWithPayload) */
export interface TestConnectionPayload {
  integrationType: string;
  credentials: {
    type: string;
    data: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  };
  integrationParameters?: Record<string, unknown>;
}

export type ConnectionStatus = "idle" | "success" | "failed";

/**
 * Shared hook for testing integration connection.
 * For GCP Vertex AI, Agent Force, and Copilot Studio discovery forms only.
 * getPayload should return the API payload or null if validation fails.
 */
export function useTestConnection(
  getPayload: () => TestConnectionPayload | null,
) {
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("idle");

  const handleTestConnection = useCallback(async () => {
    setIsTestingConnection(true);
    setConnectionStatus("idle");
    try {
      const payload = getPayload();
      if (!payload) {
        setConnectionStatus("failed");
        return;
      }
      const response = await integrationApi.testConnectionWithPayload(payload);
      setConnectionStatus(response.status === "VALID" ? "success" : "failed");
    } catch {
      setConnectionStatus("failed");
    } finally {
      setIsTestingConnection(false);
    }
  }, [getPayload]);

  return { isTestingConnection, connectionStatus, handleTestConnection };
}
